import React, { useCallback, useEffect, useMemo, useState } from "react";
import useControlPanel from "src/hooks/useControlPanel";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import Pagination from "src/components/Pagination/Pagination";
import ReleaseSpeakerVersionControlPanel from "../../components/ControlPanel/ReleaseSpeakerVersionControlPanel";
import ReleaseSpeakerVersionPanelOptional from "../../components/ControlPanel/PanelOptional/ReleaseSpeakerVersionPanelOptional";
import ReleaseSpeakerVersionTable from "../../components/Table/ReleaseSpeakerVersionTable";
import { MODE_RELEASE_SPEAKER } from "../../components/Table/Helper";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";
import { useGetPermissionAccessControl } from "src/context/PermissionAccessControl";
import {
  createReleaseSpeakerVersion,
  getReleaseSpeakerVersion,
  updateReleaseSpeakerVersion,
  updateReleaseSpeakerVersionMode,
} from "src/apis/VersionRelease/speakerVersion.api";
import { CACHE_RELEASE_SPEAKER_VERSION } from "src/constants/actionCache";
import { NotifyConfirm } from "src/components/notify/Notify";
import { path } from "src/constants/path";
import { getReleaseTypeSpeaker } from "src/apis/VersionRelease/typeSpeaker.api";
import { dataStateType } from "src/constants/status";

const initReleaseSpeakerVersionData = {
  list: [],
  total_record: 0,
};

const handleNotifyMessage = (mode, releaseSpeakerVersion) => {
  const modeRelease =
    mode === MODE_RELEASE_SPEAKER.releaseOnGroup ? "On Group" : "Mass";
  const title = `<h5>Release Version ${releaseSpeakerVersion.versionName} - Mode ${modeRelease}</h5>`;
  const text = `
    <div>
      <h5>Version: <span style="color: red;">${releaseSpeakerVersion.versionName}</span></h5>
      <h5>Mode Release: <span style="color: red;">${modeRelease}</span></h5>
      <h5 style="color: red;" > 
        Please check, before making confirmation
      </h5>
    </div>
  `;

  return { title, text };
};

const envOption = [
  {
    id: "staging",
    name: "staging",
  },
  {
    id: "production",
    name: "production",
  },
];

const ReleaseSpeakerVersion = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();
  // cache
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();

  const [releaseSpeakerVersionData, setReleaseSpeakerVersionData] = useState(
    initReleaseSpeakerVersionData
  );
  const [filterReleaseSpeakerVersions, setFilterReleaseSpeakerVersions] =
    useState([]);
  const [releaseTypeSpeakerId, setReleaseTypeSpeakerId] = useState("");

  const [environmentId, setEnvironmentId] = useState("staging");

  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const permissionsAccessControl = useGetPermissionAccessControl();
  const [errorsForm, setErrorsForm] = useState({});

  const fetchReleaseSpeakerVersions = useCallback(() => {
    getReleaseSpeakerVersion({}, (result) => {
      setReleaseSpeakerVersionData({ ...result });
      setFilterReleaseSpeakerVersions([...result.list]);
    });

    getReleaseTypeSpeaker({}, (result) => {
      setReleaseTypeSpeakerId(result.list[0].id);
    });
  }, []);

  useEffect(() => {
    fetchReleaseSpeakerVersions();
  }, [fetchReleaseSpeakerVersions]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const updateFilteredReleaseSpeakerVersion = useCallback(
    (releaseSpeakerVersions) => {
      setFilterReleaseSpeakerVersions(releaseSpeakerVersions);
    },
    []
  );

  /* ---- handle search ----- */
  const returnListReleaseSpeakerVersionType = useCallback(() => {
    if (status === dataStateType.INACTIVE) {
      return [];
    }
    if (releaseTypeSpeakerId === "all") {
      return filterReleaseSpeakerVersions;
    } else {
      return filterReleaseSpeakerVersions
        .filter(
          (releaseTypeSpeaker) =>
            releaseTypeSpeaker.typeSpeakerId === releaseTypeSpeakerId
        )
        .filter(
          (releaseTypeSpeaker) =>
            releaseTypeSpeaker.environment === environmentId
        );
    }
  }, [
    environmentId,
    filterReleaseSpeakerVersions,
    releaseTypeSpeakerId,
    status,
  ]);

  const searchByKeyWord = useCallback((releaseSpeakerVersions, key) => {
    return releaseSpeakerVersions.filter((releaseSpeakerVersion) => {
      return releaseSpeakerVersion.name
        .toLowerCase()
        .includes(key.toLowerCase().trim());
    });
  }, []);

  const handleSearch = useCallback(
    (value) => {
      const filteredReleaseSpeakerVersions = value
        ? searchByKeyWord(releaseSpeakerVersionData.list, value)
        : releaseSpeakerVersionData.list;
      updateFilteredReleaseSpeakerVersion(filteredReleaseSpeakerVersions);
    },
    [
      releaseSpeakerVersionData.list,
      searchByKeyWord,
      updateFilteredReleaseSpeakerVersion,
    ]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle add new and update ----- */
  const addOrEdit = useCallback(
    (releaseAppVersion, resetForm) => {
      if (releaseAppVersion.id) {
        updateReleaseSpeakerVersion(
          releaseAppVersion.id,
          releaseAppVersion,
          handleApiResponse
        );
      } else {
        createReleaseSpeakerVersion(releaseAppVersion, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchReleaseSpeakerVersions();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_RELEASE_SPEAKER_VERSION);
        }
      }
    },
    [fetchReleaseSpeakerVersions, setCurrentActionCache]
  );

  const handleChangeModeRelease = (
    valueRelease,
    mode,
    releaseSpeakerVersion
  ) => {
    const { title, text } = handleNotifyMessage(mode, releaseSpeakerVersion);
    if (!valueRelease) {
      NotifyConfirm(
        mode === MODE_RELEASE_SPEAKER.releaseMass ? "warning" : "info",
        text,
        (result) => {
          if (result.isConfirmed) {
            let dataUpdate = {
              typeSpeakerId: releaseSpeakerVersion.typeSpeakerId,
              isReleaseOnGroup: releaseSpeakerVersion.isReleaseOnGroup,
              isReleaseMass: releaseSpeakerVersion.isReleaseMass,
            };

            if (
              !releaseSpeakerVersion.isReleaseOnGroup &&
              !releaseSpeakerVersion.isReleaseMass
            ) {
              dataUpdate = {
                ...dataUpdate,
                isReleaseOnGroup:
                  mode === MODE_RELEASE_SPEAKER.releaseOnGroup
                    ? !valueRelease
                    : releaseSpeakerVersion.isReleaseOnGroup,
                isReleaseMass:
                  mode === MODE_RELEASE_SPEAKER.releaseMass
                    ? !valueRelease
                    : releaseSpeakerVersion.isReleaseMass,
              };
            } else {
              dataUpdate = {
                ...dataUpdate,
                isReleaseOnGroup:
                  mode === MODE_RELEASE_SPEAKER.releaseOnGroup
                    ? !valueRelease
                    : !releaseSpeakerVersion.isReleaseOnGroup,
                isReleaseMass:
                  mode === MODE_RELEASE_SPEAKER.releaseMass
                    ? !valueRelease
                    : !releaseSpeakerVersion.isReleaseMass,
              };
            }

            updateReleaseSpeakerVersionMode(
              releaseSpeakerVersion.id,
              dataUpdate,
              (_) => {
                getReleaseSpeakerVersion({}, (result) => {
                  setReleaseSpeakerVersionData({ ...result });
                  setFilterReleaseSpeakerVersions([...result.list]);
                });
                setRecordForEdit(null);
                setCurrentActionCache(CACHE_RELEASE_SPEAKER_VERSION);
              }
            );
          }
        },
        title
      );
    }
  };

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  const handleChangeEnvironment = (value) => {
    setEnvironmentId(value);
    setNumberPage(1);
  };

  const handleChangeTypeSpeaker = (value) => {
    setReleaseTypeSpeakerId(value);
    setNumberPage(1);
  };

  /* ---- result for render ----- */
  const resultFilter = useMemo(() => {
    const appVersionFilter = returnListReleaseSpeakerVersionType();
    return searchByKeyWord(appVersionFilter, searchKey);
  }, [returnListReleaseSpeakerVersionType, searchByKeyWord, searchKey]);

  const listItem = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>{permissionsAccessControl[0]?.title || ""}</h2>
      </div>

      <ReleaseSpeakerVersionControlPanel
        handleOpenPopup={handleOpenPopup}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={true}
        />
        <ReleaseSpeakerVersionPanelOptional
          releaseTypeSpeakerId={releaseTypeSpeakerId}
          environmentId={environmentId}
          envOption={envOption}
          onChangeEnvironment={handleChangeEnvironment}
          onChangeTypeSpeaker={handleChangeTypeSpeaker}
        />
      </ReleaseSpeakerVersionControlPanel>

      <ReleaseSpeakerVersionTable
        handleOpenPopup={handleOpenPopup}
        handleChangeModeRelease={handleChangeModeRelease}
        data={listItem}
        status={status}
      />

      <Pagination
        pathname={path.releaseSpeakerVersion}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default ReleaseSpeakerVersion;
